import { useEffect, useState, useRef } from 'react';
import socketService from '../services/socket.service';

interface UseSocketOptions {
  resourceType: string;
  resourceUuid: string | null;
  enabled: boolean;
  mode?: 'editor' | 'viewer';
  tabId?: string;
}

interface UseSocketReturn {
  isConnected: boolean;
  connectedUsers: any[];
  joinResource: () => void;
  leaveResource: () => void;
}

export const useSocketHook = (options: UseSocketOptions): UseSocketReturn & { disconnectSocket: () => void } => {
  const { resourceType, resourceUuid, enabled, mode = 'viewer', tabId } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectedUsers, setConnectedUsers] = useState<any[]>([]);
  const hasJoinedRef = useRef(false);
  const connectionAttemptedRef = useRef(false);

  // Function to join resource
  const joinResource = () => {
    if (!resourceUuid || !socketService.isConnected()) {
      return;
    }

    socketService.joinResource(resourceType, resourceUuid, { mode, tabId });
    hasJoinedRef.current = true;
  };

  // Function to lock tab (after successful join)
  const lockTabAfterJoin = () => {
    if (!resourceUuid || !tabId) {
      return;
    }
    socketService.lockTab(resourceType, resourceUuid, tabId);
  };

  // Function to leave resource
  const leaveResource = () => {
    if (!hasJoinedRef.current) {
      return;
    }
    // Call without params - leaveResource will use stored context
    socketService.leaveResource();
    hasJoinedRef.current = false;
  };

  const disconnectSocket = () => {
    console.log('[useSocketHook] Manual disconnect called');
    leaveResource();  // leave current resource first
    if (socketService.isConnected()) {
      socketService.disconnect();
    }
    setIsConnected(false);
    setConnectedUsers([]);
    connectionAttemptedRef.current = false;
  };


  useEffect(() => {
    if (!enabled || !resourceUuid) {
      // Reset state when disabled
      setConnectedUsers([]);
      setIsConnected(false);
      connectionAttemptedRef.current = false;

      // Disconnect socket when enabled becomes false
      if (!enabled && socketService.isConnected()) {
        console.log('[useSocketHook] enabled=false, disconnecting socket');
        socketService.disconnect();
      }
      return;
    }

    // Connect to socket if not already connected (only once)
    if (!socketService.isConnected() && !connectionAttemptedRef.current) {
      console.log('[useSocketHook] Connecting to socket');
      socketService.connect();
      connectionAttemptedRef.current = true;
    }

    // Set up connection status callback
    socketService.onConnectionChange = (status) => {
      const connected = status === 'connected';
      setIsConnected(connected);

      // Auto-join when connected
      if (connected && !hasJoinedRef.current && resourceUuid) {
        setTimeout(() => joinResource(), 100);
      }
    };

    // Set up join success callback to auto-lock tab if tabId provided
    if (tabId && mode === 'editor') {
      socketService.onResourceJoinedSuccess = () => {
        lockTabAfterJoin();
      };
    }

    // Check if already connected and join immediately
    if (socketService.isConnected() && !hasJoinedRef.current) {
      joinResource();
    }

    // Cleanup function
    return () => {
      console.log('Cleanup - leaving resource (socket persists)');
      leaveResource();

      // Clear callbacks
      socketService.onConnectionChange = null;
      socketService.onResourceJoinedSuccess = null;
      socketService.onAllUsers = null;
    };
  }, [enabled, resourceUuid, resourceType, mode, tabId]);

  return {
    isConnected,
    connectedUsers,
    joinResource,
    leaveResource,
    disconnectSocket,
  };
};
